"use client"

import type { MainModule } from "clipper2-wasm/dist/clipper2z"

let clipperInstance: MainModule | null = null
let loadingPromise: Promise<MainModule> | null = null

/**
 * Initialize and load the Clipper2Z WASM module
 * This is called automatically on first use, but can be called explicitly
 */

export async function initializeClipper(): Promise<MainModule> {
    // Return existing instance if already loaded.
    if (clipperInstance) return clipperInstance

    // Return existing promise if currently loading.
    if (loadingPromise) return loadingPromise

    // Create new loading promise with dynamic import.
    loadingPromise = (async () => {
        const { default: Clipper2ZFactory } = await import("clipper2-wasm/dist/es/clipper2z")
        return Clipper2ZFactory({
            locateFile: (path: string) => {
                // Note: `path` should match the name of the .wasm file in the Next.js public/ folder.
                return `/${path}`
            },
        })
    })()

    try {
        clipperInstance = await loadingPromise
        return clipperInstance
    } finally {
        loadingPromise = null
    }
}

/**
 * Get the Clipper2Z instance synchronously
 * Throws if WASM module is not yet loaded
 */
const getClipperSync = (): MainModule => {
    if (!clipperInstance) {
        throw new Error(
            "Clipper2Z WASM module not loaded. Call initializeClipper() first and await it."
        )
    }
    return clipperInstance
}

export const initializeClipperFromBinary = async (module: WebAssembly.Module) => {
    if (clipperInstance) return
    if (loadingPromise) return await loadingPromise
    loadingPromise = (async () => {
        const { default: Clipper2ZFactory } = await import("clipper2-wasm/dist/es/clipper2z")
        const instance = await Clipper2ZFactory({
            locateFile: () => {
                return ""
            },
            instantiateWasm: async (
                info: WebAssembly.Imports,
                receiveModule: (module: WebAssembly.Module) => void
            ) => {
                const instance = await WebAssembly.instantiate(module, info)
                receiveModule(instance)
            },
        })
        clipperInstance = instance
        return instance
    })()
    await loadingPromise
    loadingPromise = null
}

/**
 * Calculate the signed area of a polygon ring
 * Positive = counter-clockwise, Negative = clockwise
 */
const calculateSignedArea = (ring: [number, number][]): number => {
    let area = 0

    for (let i = 0; i < ring.length; i++) {
        const j = (i + 1) % ring.length
        area += ring[i][0] * ring[j][1]
        area -= ring[j][0] * ring[i][1]
    }

    return area / 2
}

/**
 * Ensure a polygon ring has counter-clockwise winding (positive area)
 * This normalizes all rings to the same orientation for proper union
 */
const ensureCounterClockwise = (ring: [number, number][]): [number, number][] => {
    if (calculateSignedArea(ring) < 0) {
        return [...ring].reverse()
    }

    return ring
}

/**
 * Union (merge) multiple polygons into a single geometry
 * @param polygons Array of polygons, where each polygon is [number, number][]
 * @param precision Decimal precision for Clipper2 internal operations (default 6)
 * @returns Merged polygons as [number, number][][]
 */
export function unionPolygons(polygons: [number, number][][], precision = 6): [number, number][][] {
    const Clipper2Z = getClipperSync()

    try {
        // Create a PathsD collection with all input polygons.
        const paths = new Clipper2Z.PathsD()

        for (const polygon of polygons) {
            // Normalize winding direction to counter-clockwise for all polygons.
            // This ensures all polygons are treated as filled areas, not holes.
            const normalizedCoordinates = ensureCounterClockwise(polygon)

            const coords: number[] = []
            for (const [x, y] of normalizedCoordinates) {
                coords.push(x, y)
            }
            const path = Clipper2Z.MakePathD(coords)
            paths.push_back(path)
            path.delete()
        }

        // Perform union operation using NonZero fill rule.
        // With normalized CCW winding, NonZero will properly merge all polygons.
        const solution = Clipper2Z.UnionSelfD(paths, Clipper2Z.FillRule.NonZero, precision)

        // Convert result back to [number, number][][]
        const result: [number, number][][] = []
        for (let i = 0; i < solution.size(); i++) {
            const path = solution.get(i)
            const ring: [number, number][] = []
            for (let j = 0; j < path.size(); j++) {
                const point = path.get(j)
                ring.push([point.x, point.y])
            }
            if (ring.length >= 3) {
                result.push(ring)
            }
        }

        // Clean up WASM memory.
        paths.delete()
        solution.delete()

        return result
    } catch (err) {
        console.error("Error performing union:", err)
        throw err
    }
}

/**
 * Join type options for polygon buffering
 * Values must match Clipper2Z WASM JoinType enum: { Square: 0, Round: 2, Miter: 3 }
 */
enum JoinType {
    Square = 0,
    Round = 2,
    Miter = 3,
}

/**
 * Buffer (expand or shrink) a polygon by a specified distance
 *
 * @param points Polygon vertices as [number, number][] coordinates
 * @param distance Distance to buffer (positive = expand outward, negative = shrink inward)
 * @param options Optional configuration for the buffer operation
 * @returns Buffered polygon(s) as [number, number][][] (may return multiple polygons if shrinking splits the shape)
 *
 * @example
 * // Expand a triangle by 10 units
 * const expanded = bufferPolygon([[0, 0], [100, 0], [50, 100]], 10)
 *
 * @example
 * // Shrink a square by 5 units with mitered corners
 * const shrunk = bufferPolygon([[0, 0], [100, 0], [100, 100], [0, 100]], -5, { joinType: JoinType.Miter })
 */
export function bufferPolygon(
    points: [number, number][],
    distance: number,
    options?: {
        /** Type of join at vertices (default: Round) */
        joinType?: JoinType
        /** Limit on miter join length when using Miter join type (default: 2.0) */
        miterLimit?: number
        /** Decimal precision for internal Clipper2 operations (default: 2) */
        precision?: number
    }
): [number, number][][] {
    const Clipper2Z = getClipperSync()

    const { joinType = JoinType.Round, miterLimit = 2.0, precision = 2 } = options ?? {}

    try {
        if (points.length < 3) {
            throw new Error("Polygon must have at least 3 points")
        }

        // Ensure polygon is properly closed
        const cleanPoints: [number, number][] = [...points]
        const first = cleanPoints[0]
        const last = cleanPoints[cleanPoints.length - 1]
        if (first[0] !== last[0] || first[1] !== last[1]) {
            cleanPoints.push([first[0], first[1]])
        }

        // Use PathsD (double-precision floating point) to avoid integer overflow issues
        // with large Web Mercator coordinates
        const coords: number[] = []
        for (const [x, y] of cleanPoints) {
            coords.push(x, y)
        }

        const inputPath = Clipper2Z.MakePathD(coords)
        const inputPaths = new Clipper2Z.PathsD()
        inputPaths.push_back(inputPath)

        // Arc tolerance controls smoothness of rounded corners
        // Higher value = fewer vertices, less smooth arcs
        // Using distance/5 for coarser arcs with fewer vertices
        const arcTolerance = 5

        // Map our JoinType enum to actual Clipper2Z enum objects
        const clipperJoinType =
            joinType === JoinType.Square
                ? Clipper2Z.JoinType.Square
                : joinType === JoinType.Miter
                  ? Clipper2Z.JoinType.Miter
                  : Clipper2Z.JoinType.Round

        // Perform the buffer/offset operation using double-precision paths
        // InflatePathsD(paths, delta, joinType, endType, miterLimit, precision, arcTolerance)
        const solution = Clipper2Z.InflatePathsD(
            inputPaths,
            distance,
            clipperJoinType,
            Clipper2Z.EndType.Polygon,
            miterLimit,
            precision,
            arcTolerance
        )

        // Convert result back to coordinate arrays
        const result: [number, number][][] = []
        for (let i = 0; i < solution.size(); i++) {
            const path = solution.get(i)
            const ring: [number, number][] = []
            for (let j = 0; j < path.size(); j++) {
                const point = path.get(j)
                ring.push([point.x, point.y])
            }
            if (ring.length >= 3) {
                result.push(ring)
            }
        }

        // Clean up WASM memory
        inputPath.delete()
        inputPaths.delete()
        solution.delete()

        return result
    } catch (err) {
        console.error("Error buffering polygon:", err)
        throw err
    }
}
